import { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import Title from '../../../components/UI/Title/Title';
import Filters from '../../../components/Container/Filters/Filters';
import EnhancedTable from '../../../components/UI/RcTable/EnhancedTable';
import useTranslations from '../../../hooks/useTranslations';
import useUrlSearchParam from '../../../hooks/useUrlSearchParams';
import { useAppSelector } from '../../../store';
import { TableActions } from '../../../store/entities/table/table.actions';
import { DeliveriesActions } from '../../../store/entities/deliveries/deliveries.actions';
import { IDelivery } from '../../../models/delivery';
import { IAutoCompleteItem } from '../../../models/autoComplete';
import { FieldTypes, IFormFields } from '../../../models/form';
import { CONSTANTS } from '../../../utils/constants';
import { getDateTimeAndSeconds } from '../../../utils/formatters';
import { GeneralActions } from '../../../store/entities/general/general.actions';
import { SitesActions } from '../../../store/entities/sites/sites.actions';
import { ISite } from '../../../models/site';
import { ICity, IDynamicList } from '../../../store/entities/general/type';
import { checkArraysAreTheSame } from '../../../utils/helpers';
import { LockersActions } from '../../../store/entities/lockers/lockers.actions';
import { deliveriesListSearchValidation } from '../../../utils/formValidation/deliveriesList';
import Button from '../../../components/UI/Button/Button';
import Dialog from '../../../components/UI/Dialog/Dialog';

export enum DeliveryStatus {
  SUCCESSFUL = 1,
  CANCELLED = 2,
}

const DeliveriesList = ({
  deliveryStatus,
}: {
  deliveryStatus: DeliveryStatus;
}) => {
  const dispatch = useDispatch();
  const { rcTranslate } = useTranslations();
  const { getValueFromQuery, params } = useUrlSearchParam();

  const { deliveries } = useAppSelector((state) => state.deliveries);
  const { lockers } = useAppSelector((state) => state.lockers);
  const { siteListDetails, closets } = useAppSelector((state) => state.sites);
  const {
    cities,
    orderStatuses,
    paymentStatuses,
    appLanguage,
    compoundsDetails,
    deliveryCancelReasons,
  } = useAppSelector((state) => state.general);
  const { sortModel, nextPageNumber, currentPage } = useAppSelector(
    (state) => state.table
  );

  const [rows, setRows] = useState<any[]>([]);
  const [siteList, setSiteList] = useState<IAutoCompleteItem[]>([]);
  const [cityList, setCityList] = useState<IAutoCompleteItem[]>([]);
  const [orderStatusList, setOrderStatusList] = useState<IAutoCompleteItem[]>([]);
  const [paymentStatusList, setPaymentStatusList] = useState<IAutoCompleteItem[]>([]);
  const [deliveryCancelReasonList, setDeliveryCancelReasonList] = useState<IAutoCompleteItem[]>([]);
  const [packageImageUrl, setPackageImageUrl] = useState<string | null>(null);

  useEffect(() => {
    params.set('deliveryStatus', deliveryStatus.toString());
  }, [params, deliveryStatus, dispatch]);

  useEffect(() => {
    // initial fetch
    dispatch(GeneralActions.getCities());
    dispatch(GeneralActions.getOrderStatuses());
    dispatch(GeneralActions.getPaymentStatuses());
    dispatch(GeneralActions.getDeliveryCancelReasons());
    dispatch(SitesActions.getSiteListDetails());
    dispatch(SitesActions.getClosets(undefined, undefined, false));
    dispatch(LockersActions.getLockers(undefined, undefined, false));
  }, [dispatch]);

  useEffect(() => {
    // reset to page 1 when filters change
    if (currentPage !== 1) {
      dispatch(TableActions.setTableData({ currentPage: 1 }));
    }
    dispatch(DeliveriesActions.getDeliveries(params));
  }, [params, deliveryStatus, dispatch]);

  useEffect(() => {
    if (nextPageNumber) {
      const getData = async () => {
        await dispatch(
          DeliveriesActions.getDeliveries(
            params,
            sortModel,
            false,
            nextPageNumber
          )
        );
        await dispatch(TableActions.setTableData({ nextPageNumber: null }));
      };
      getData();
    }
  }, [nextPageNumber, sortModel, dispatch, params]);

  useEffect(() => {
    if (sortModel.length) {
      const getData = async () => {
        await dispatch(
          DeliveriesActions.getDeliveries(params, sortModel, false)
        );
        await dispatch(TableActions.setTableData({ nextPageNumber: null }));
      };
      getData();
    }
  }, [sortModel, dispatch, params]);

  useEffect(() => {
    if (siteListDetails.length) {
      siteListDetails.sort((a, b) => (a.id > b.id ? 1 : -1));
      const newSites: IAutoCompleteItem[] = siteListDetails.map(
        (site: ISite) => ({
          id: site.id,
          label: `${site.id} - ${
            (appLanguage === CONSTANTS.DEFAULT_LANGUAGE
              ? site.nameHE
              : site.nameEN) ?? ''
          }`,
        })
      );
      setSiteList(newSites);
    }
  }, [appLanguage, siteListDetails]);

  useEffect(() => {
    if (cities.length) {
      const newCities: IAutoCompleteItem[] = cities.map((city: ICity) => ({
        id: city.id,
        label:
          (appLanguage === CONSTANTS.DEFAULT_LANGUAGE
            ? city.nameHE
            : city.nameEN) ?? '',
      }));
      setCityList(newCities);
    }
  }, [appLanguage, cities]);

  useEffect(() => {
    if (orderStatuses.length) {
      const list: IAutoCompleteItem[] = orderStatuses.map((os: IDynamicList) => ({
        id: os.id,
        label:
          (appLanguage === CONSTANTS.DEFAULT_LANGUAGE
            ? os.nameHE
            : os.nameEN) ?? '',
      }));
      setOrderStatusList(list);
    }
  }, [appLanguage, orderStatuses]);

  useEffect(() => {
    if (paymentStatuses.length) {
      const list: IAutoCompleteItem[] = paymentStatuses.map((ps: IDynamicList) => ({
        id: ps.id,
        label:
          (appLanguage === CONSTANTS.DEFAULT_LANGUAGE
            ? ps.nameHE
            : ps.nameEN) ?? '',
      }));
      setPaymentStatusList(list);
    }
  }, [appLanguage, paymentStatuses]);

  useEffect(() => {
    if (deliveryCancelReasons.length) {
      const list: IAutoCompleteItem[] = deliveryCancelReasons.map((dcr: IDynamicList) => ({
        id: dcr.id,
        label:
          (appLanguage === CONSTANTS.DEFAULT_LANGUAGE
            ? dcr.nameHE
            : dcr.nameEN) ?? '',
      }));
      setDeliveryCancelReasonList(list);
    }
  }, [appLanguage, deliveryCancelReasons]);

  useEffect(() => {
    if (deliveries?.length) {
      let compoundsIds = deliveries.map((delivery: IDelivery) => {
        return delivery?.compoundId;
      });

      compoundsIds = Array.from(new Set(compoundsIds));

      const compoundsDetailsIds = compoundsDetails.map((i) => i.id);

      if (checkArraysAreTheSame(compoundsDetailsIds, compoundsIds)) return;

      dispatch(GeneralActions.getCompoundsByIds(compoundsIds));
    }
  }, [dispatch, deliveries, compoundsDetails]);

  const mapDeliveriesToRows = useCallback(
    (data: IDelivery[]) => {
      return data.map((delivery: IDelivery, index: number) => {
        const cityName = cities.find((c: ICity) => c.id === delivery.cityId);
        const siteName = siteListDetails.find(
          (s: ISite) => s.id === delivery.siteId
        );
        const compoundName = compoundsDetails.find(
          (c) => c.id === delivery.compoundId
        );

        const closetName = closets.find(
          (c) => c.id == delivery.closetId
        )?.nickName;

        const cellNumber = lockers.find(
          (c) => c.id === delivery.cellId
        )?.cellNumber;

        const orderStatusOpt = orderStatuses.find(
          (os: IDynamicList) => os.id === delivery.orderStatus
        );
        const paymentStatusOpt = paymentStatuses.find(
          (ps: IDynamicList) => ps.id === delivery.paymentStatus
        );
        const deliveryCancelReasonOpt = deliveryCancelReasons.find(
          (dcr: IDynamicList) => dcr.id === delivery.deliveryCancelReason
        );

        const initiatorDetailsURI = delivery.initiatorCustomerId
          ? `/customers/list?customerId=${delivery.initiatorCustomerId}`
          : delivery.initiatorPhoneNumber
            ? `/customers/list?phoneNumber=${delivery.initiatorPhoneNumber}`
            : '';
        const collectorDetailsURI = delivery.collectorId
          ? `/customers/list?customerId=${delivery.collectorId}`
          : '';
        const shippingCompanyURI = delivery.initiatorCompanyId
          ? `/${CONSTANTS.ROUTES.DELIVERIES_MANAGEMENT.BASE}/${CONSTANTS.ROUTES.DELIVERIES_MANAGEMENT.CHILDREN.SHIPPING_COMPANIES_MANAGEMENT}?shippingCompanyId=${delivery.initiatorCompanyId}`
          : '';
        const rowsData = {
          id: index,
          created: delivery.created && getDateTimeAndSeconds(delivery.created),
          cityId: cityName
            ? ((appLanguage === CONSTANTS.DEFAULT_LANGUAGE
                ? cityName.nameHE
                : cityName.nameEN) ?? '')
            : '',
          siteId: siteName
            ? `${siteName.id} - ${
                (appLanguage === CONSTANTS.DEFAULT_LANGUAGE
                  ? siteName.nameHE
                  : siteName.nameEN) ?? ''
              }`
            : '',
          compoundId:
            (compoundName
              ? ((appLanguage === CONSTANTS.DEFAULT_LANGUAGE
                  ? compoundName.nameHE
                  : compoundName.nameEN) ?? '')
              : delivery.compoundId) || '',
          closetId: closetName || delivery.closetId,
          cellId: cellNumber || delivery.cellId || '',
          startDate:
            delivery.startDate && getDateTimeAndSeconds(delivery.startDate),
          endDate: delivery.endDate && getDateTimeAndSeconds(delivery.endDate),
          customer:
            delivery.operatingCustomerId || delivery.payingCustomerId ? (
              <a
                href={`/customers/list?customerId=${delivery.operatingCustomerId || delivery.payingCustomerId}`}
              >
                {rcTranslate('deliveries.columns.customerDetails')}
              </a>
            ) : (
              ''
            ),
          initiator: initiatorDetailsURI ? (
            <a href={initiatorDetailsURI}>
              {rcTranslate('deliveries.columns.initiatorDetails')}
            </a>
          ) : (
            ''
          ),
          shippingCompany: shippingCompanyURI ? (
            <a href={shippingCompanyURI}>
              {rcTranslate('deliveries.columns.shippingCompanyDetails')}
            </a>
          ) : (
            ''
          ),
          shippingOrderId: delivery.shippingOrderId ?? '',
        };
        if (deliveryStatus === DeliveryStatus.SUCCESSFUL) {
          Object.assign(rowsData, {
            orderStatus: orderStatusOpt
              ? ((appLanguage === CONSTANTS.DEFAULT_LANGUAGE
                  ? orderStatusOpt.nameHE
                  : orderStatusOpt.nameEN) ?? '')
              : '',
            paymentStatus: paymentStatusOpt
              ? ((appLanguage === CONSTANTS.DEFAULT_LANGUAGE
                  ? paymentStatusOpt.nameHE
                  : paymentStatusOpt.nameEN) ?? '')
              : '',
            collector: collectorDetailsURI ? (
              <a href={collectorDetailsURI}>
                {rcTranslate('deliveries.columns.collectorDetails')}
              </a>
            ) : (
              ''
            ),
            packageImage: delivery.orderId ? (
              <Button
                onClick={async () => {
                  const url = await dispatch(
                    DeliveriesActions.getPackageImage(delivery.orderId!)
                  );
                  if (typeof url === 'string') {
                    setPackageImageUrl(url);
                  } else {
                    alert(rcTranslate('deliveries.messages.noPackageImage'));
                  }
                }}
                title={rcTranslate('deliveries.columns.viewPackageImage')}
              />
            ) : (
              ''
            ),
          });
        } else {
          Object.assign(rowsData, {
            deliveryCancelReason: deliveryCancelReasonOpt
              ? ((appLanguage === CONSTANTS.DEFAULT_LANGUAGE
                  ? deliveryCancelReasonOpt.nameHE
                  : deliveryCancelReasonOpt.nameEN) ?? '')
              : '',
            deliveryCancelReasonFreeText:
              delivery.deliveryCancelReasonFreeText || '',
          });
        }
        return rowsData;
      });
    },
    [
      cities,
      siteListDetails,
      compoundsDetails,
      closets,
      lockers,
      orderStatuses,
      paymentStatuses,
      appLanguage,
    ]
  );

  useEffect(() => {
    if (!deliveries?.length) return setRows([]);
    setRows(mapDeliveriesToRows(deliveries));
  }, [deliveries, mapDeliveriesToRows]);

  const columns = [
    {
      id: 'created',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('deliveries.columns.created'),
    },
    {
      id: 'cityId',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('deliveries.columns.city'),
    },
    {
      id: 'siteId',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('deliveries.columns.site'),
    },
    {
      id: 'compoundId',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('deliveries.columns.compound'),
    },
    {
      id: 'closetId',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('deliveries.columns.closet'),
    },
    {
      id: 'cellId',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('deliveries.columns.cell'),
    },
    {
      id: 'startDate',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('deliveries.columns.startDate'),
    },
    {
      id: 'endDate',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('deliveries.columns.endDate'),
    },
    {
      id: 'customer',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('deliveries.columns.customer'),
    },
    {
      id: 'initiator',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('deliveries.columns.initiator'),
    },
    {
      id: 'shippingCompany',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('deliveries.columns.shippingCompany'),
    },
    {
      id: 'shippingOrderId',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('deliveries.columns.shippingOrderId'),
    },
    ...(deliveryStatus === DeliveryStatus.SUCCESSFUL
      ? [
          {
            id: 'orderStatus',
            numeric: false,
            disablePadding: true,
            label: rcTranslate('deliveries.columns.orderStatus'),
          },
          {
            id: 'paymentStatus',
            numeric: false,
            disablePadding: true,
            label: rcTranslate('deliveries.columns.paymentStatus'),
          },
          {
            id: 'collector',
            numeric: false,
            disablePadding: true,
            label: rcTranslate('deliveries.columns.collector'),
          },
          {
            id: 'packageImage',
            numeric: false,
            disablePadding: true,
            label: rcTranslate('deliveries.columns.packageImage'),
          },
        ]
      : [
          {
            id: 'deliveryCancelReason',
            numeric: false,
            disablePadding: true,
            label: rcTranslate('deliveries.columns.deliveryCancelReason'),
          },
          {
            id: 'deliveryCancelReasonFreeText',
            numeric: false,
            disablePadding: true,
            label: rcTranslate(
              'deliveries.columns.deliveryCancelReasonFreeText'
            ),
          },
        ]),
  ];

  const searchFields: IFormFields = {
    initialValues: {
      cityId: getValueFromQuery('cityId', 'number'),
      siteId: getValueFromQuery('siteId', 'number'),
      shippingOrderId: getValueFromQuery('shippingOrderId'),
      fromDate: getValueFromQuery('fromDate'),
      toDate: getValueFromQuery('toDate'),
      ...(deliveryStatus === DeliveryStatus.SUCCESSFUL
        ? {
            orderStatus: getValueFromQuery('orderStatus', 'number'),
            paymentStatus: getValueFromQuery('paymentStatus', 'number'),
          }
        : {
            deliveryCancelReason: getValueFromQuery('deliveryCancelReason', 'number'),
          }),
    },
    formData: [
      {
        type: FieldTypes.CHILDREN,
        id: 'externalFieldsWrapper',
        fields: [
          {
            type: FieldTypes.AUTOCOMPLETE,
            id: 'cityId',
            label: 'deliveries.filters.city',
            items: cityList,
            props: { multiple: false },
          },
          {
            type: FieldTypes.AUTOCOMPLETE,
            id: 'siteId',
            label: 'deliveries.filters.site',
            items: siteList,
            props: { multiple: false },
          },
          {
            type: FieldTypes.DATE,
            id: 'fromDate',
            label: 'deliveries.filters.fromDate',
            props: { getStartOfDay: true },
          },
          {
            type: FieldTypes.DATE,
            id: 'toDate',
            label: 'deliveries.filters.toDate',
            props: { getFullDayTime: true, numOfDays: 1 },
          },
        ],
      },
      {
        type: FieldTypes.CHILDREN,
        id: 'extendedFieldsWrapper',
        fields: [
          {
            type: FieldTypes.CHILDREN,
            id: 'extendedFieldsContainer',
            fields: [
              {
                type: FieldTypes.CHILDREN,
                id: 'extendedFields',
                className: 'filters__extendedFields',
                fields: [
                  {
                    type: FieldTypes.TEXT,
                    id: 'shippingOrderId',
                    label: 'deliveries.filters.shippingOrderId',
                  },
                  ...(deliveryStatus === DeliveryStatus.SUCCESSFUL
                    ? [
                        {
                          type: FieldTypes.AUTOCOMPLETE,
                          id: 'orderStatus',
                          label: 'deliveries.filters.orderStatus',
                          items: orderStatusList,
                          props: { multiple: false },
                        },
                        {
                          type: FieldTypes.AUTOCOMPLETE,
                          id: 'paymentStatus',
                          label: 'deliveries.filters.paymentStatus',
                          items: paymentStatusList,
                          props: { multiple: false },
                        },
                      ]
                    : [
                        {
                          type: FieldTypes.AUTOCOMPLETE,
                          id: 'deliveryCancelReason',
                          label: 'deliveries.filters.deliveryCancelReason',
                          items: deliveryCancelReasonList,
                          props: { multiple: false },
                        },
                      ]),
                ],
              },
              {
                type: FieldTypes.CHILDREN,
                id: 'actionsContainer',
                className: 'filters__extendedActions',
                fields: [
                  {
                    type: FieldTypes.BUTTON,
                    id: 'closeExtendedSearch',
                    label: 'searchFields.closeExtendedSearch',
                  },
                  {
                    type: FieldTypes.CHILDREN,
                    id: 'submitButtonWrapper',
                    className: 'filters__submitButtonWrapper',
                    fields: [
                      {
                        type: FieldTypes.SUBMIT_BUTTON,
                        id: 'submitButton',
                        label: '',
                      },
                      {
                        type: FieldTypes.BUTTON,
                        id: 'cleanSearch',
                        label: 'searchFields.cleanSearch',
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };

  return (
    <>
      <Title text={rcTranslate('deliveries.pageTitle')} />
      <Filters
        fields={searchFields}
        filtersValidation={deliveriesListSearchValidation}
        isExtended={false}
      />
      <EnhancedTable
        rows={rows}
        columns={columns}
        sortDataByDate={() => {}}
        checkboxSelection={false}
        expandable={false}
        showId={false}
      />
      {packageImageUrl && (
        <Dialog
          title={'deliveries.columns.packageImage'}
          body={
            <img
              src={packageImageUrl}
              alt={rcTranslate('deliveries.columns.packageImage')}
              title={rcTranslate('deliveries.columns.packageImage')}
              onClick={() => window.open(packageImageUrl, '_blank', 'noopener')}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                display: 'block',
                margin: '0 auto',
                objectFit: 'contain',
                cursor: 'pointer',
              }}
            />
          }
          onClose={() => setPackageImageUrl(null)}
          showFooter={false}
        />
      )}
    </>
  );
};

export const SuccessfulDeliveriesList = () => (
  <DeliveriesList deliveryStatus={DeliveryStatus.SUCCESSFUL} />
);

export const CancelledDeliveriesList = () => (
  <DeliveriesList deliveryStatus={DeliveryStatus.CANCELLED} />
);

export default DeliveriesList;
